import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getDefaultJsxScope, getModuleRegistry } from './jsxScope';
import { ThemeProvider } from '@emotion/react';
import { Box, CssBaseline, useTheme } from '@mui/material';
import { MarkdownExtended } from '..';
import ChartSkeleton from '../Library/VizLibrary/Skeletons/ChartSkeleton';
import MetricSkeleton from '../Library/VizLibrary/Skeletons/MetricSkeleton';
import MetricGroupSkeleton from '../Library/VizLibrary/Skeletons/MetricGroupSkeleton';
import TableSkeleton from '../Library/VizLibrary/Skeletons/TableSkeleton';
import IFrameSkeleton from '../Library/VizLibrary/Skeletons/IFrameSkeleton';
import GeneratingVisualization from './GeneratingVisualization';

/**
 * Server-emitted sentinel inside a ```liveTsx fence that signals the
 * block's content should be abandoned (compile failed server-side,
 * retry incoming). LiveJsxBlock renders nothing when present — the
 * next ```liveTsx fence opens a fresh block. Keep in sync with the
 * .NET WSServer's JsxLiveStreamProcessor.
 */
export const LIVE_TSX_INVALID_SENTINEL = '<!--liveTsx:invalid-->';

interface LiveJsxBlockProps {
    /**
     * Server-compiled JS function body. The .NET WSServer's
     * `JsxLiveStreamProcessor` runs every closing ```liveTsx fence through
     * the tsx-sidecar's `build` method (type-check + esbuild transform +
     * scope-binding rewrite) before it reaches the chat markdown. By the
     * time we see it here, `code` is already valid JS of the shape:
     *
     *   var React = __scope['react'];
     *   var Box   = __scope['@mui/material'].Box;
     *   function MyComponent() { return React.createElement(Box, ...); }
     *   return MyComponent;
     *
     * — exactly what `new Function('__scope', code)(scope)` needs to
     * return the React component. No sucrase, no regex import rewriter,
     * no main-thread compile pause per block.
     */
    code: string;
    /** Extra scope entries merged on top of the default scope */
    scope?: Record<string, unknown>;
    partial: boolean;
    /**
     * 0..1 progress hint for partial blocks. Surfaces in the placeholder
     * (`GeneratingVisualization`) as filled bar buckets — see parseBlocks
     * for the tick-marker → fraction derivation.
     */
    progress?: number;
    bytes?: number;
}

// ---------------------------------------------------------------------------
// Compilation
// ---------------------------------------------------------------------------

/**
 * Pre-flight scan: every `__scope["specifier"]` and `__scope["specifier"].Prop`
 * access in the bundled JS is matched against the runtime scope BEFORE the
 * function is invoked. Missing modules and missing named exports get
 * collected into a single human-readable error message so the user sees
 * "LiveTsx: missing 'Chart' on '@envision/ui-library'" instead of the
 * downstream "Cannot read properties of undefined" / "Element type is
 * invalid" cascade that's the actual symptom.
 *
 * Hosts wire missing modules through `extendModuleRegistry(specifier, exports)`.
 */
function checkScopeBindings(
    serverJs: string,
    scope: Record<string, unknown>,
): Error | null {
    // Module bindings: `__scope["mod"]` (no property access) — typical first line
    // of a compiled snippet, e.g. `var UILibrary = __scope["@envision/ui-library"];`.
    // Property bindings: `__scope["mod"].Prop` — e.g. `var Chart = __scope["@envision/ui-library"].Chart;`.
    const accessRe = /__scope\[(["'`])([^"'`\]]+)\1\](?:\.([A-Za-z_$][\w$]*))?/g;

    const missingModules = new Set<string>();
    const missingExports = new Map<string, Set<string>>();
    let match: RegExpExecArray | null;

    while ((match = accessRe.exec(serverJs)) !== null) {
        const specifier = match[2];
        const propName = match[3];

        if (!(specifier in scope)) {
            missingModules.add(specifier);
            continue;
        }
        if (propName) {
            const mod = scope[specifier] as Record<string, unknown> | null | undefined;
            if (mod == null || !(propName in mod)) {
                if (!missingExports.has(specifier)) missingExports.set(specifier, new Set());
                missingExports.get(specifier)!.add(propName);
            }
        }
    }

    if (missingModules.size === 0 && missingExports.size === 0) return null;

    const lines: string[] = ['LiveTsx: bundled JS references bindings that are not in the scope.'];
    if (missingModules.size > 0) {
        lines.push('');
        lines.push('Unregistered modules:');
        for (const m of missingModules) lines.push(`  • "${m}"`);
        lines.push('  Register via extendModuleRegistry("<specifier>", { ...exports }) before mounting the chat.');
    }
    if (missingExports.size > 0) {
        lines.push('');
        lines.push('Missing named exports:');
        for (const [mod, props] of missingExports) {
            lines.push(`  • "${mod}" → ${Array.from(props).map(p => `'${p}'`).join(', ')}`);
        }
        lines.push('  Add these via extendModuleRegistry("' + Array.from(missingExports.keys())[0] + '", { Chart, Metric, … }).');
    }
    return new Error(lines.join('\n'));
}

/**
 * Realize the server-compiled function body into a React component by
 * wrapping it as `function(__scope) { … }` and immediately calling with
 * the runtime scope. The body is responsible for the `return Component;`
 * at the bottom (the sidecar always appends it).
 */
function compileToComponent(
    serverJs: string,
    scope: Record<string, unknown>,
): React.ComponentType {
    const bindingError = checkScopeBindings(serverJs, scope);
    if (bindingError) throw bindingError;
    // eslint-disable-next-line no-new-func
    const factory = new Function('__scope', serverJs);
    return factory(scope);
}

// ---------------------------------------------------------------------------
// Skeleton detection for partial code blocks
// ---------------------------------------------------------------------------
//
// A `partial: true` render today should be rare — the server only emits
// the markdown fence after `build` completes, so by the time the client
// has any content it's already valid compiled JS. The partial branch
// stays for two cases: (a) historical session messages stored mid-fence
// before Phase 2; (b) test surfaces that mount LiveJsxBlock directly with
// in-flight TSX. Both render as a Skeleton, never as executable code.

const SKELETON_TAG_RE = /<(chart|containtable|metricgroup|metric|iframe)\b/i;

const SKELETON_MAP: Record<string, React.FC> = {
    chart: ChartSkeleton,
    containtable: TableSkeleton,
    metricgroup: MetricGroupSkeleton,
    metric: MetricSkeleton,
    iframe: IFrameSkeleton,
};

function detectSkeleton(code: string): React.FC | null {
    const match = SKELETON_TAG_RE.exec(code);
    if (!match) return null;
    const tag = match[1].toLowerCase();
    if (tag === 'metric' && /metricgroup/i.test(code)) return MetricGroupSkeleton;
    return SKELETON_MAP[tag] ?? null;
}

// ---------------------------------------------------------------------------
// Error boundary
// ---------------------------------------------------------------------------

export class JsxErrorBoundary extends React.Component<
    { children: React.ReactNode; fallback: (error: Error) => React.ReactNode },
    { error: Error | null }
> {
    state: { error: Error | null } = { error: null };

    static getDerivedStateFromError(error: Error) {
        return { error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.error('[LiveJsxBlock] Render error:', error, info);
    }

    render() {
        if (this.state.error) return this.props.fallback(this.state.error);
        return this.props.children;
    }
}

// ---------------------------------------------------------------------------
// LiveJsxBlock
// ---------------------------------------------------------------------------

const LiveJsxBlock: React.FC<LiveJsxBlockProps> = ({ code, partial, scope: extraScope, progress = 0, bytes=0 }) => {
    // `__scope` for the compiled IIFE merges:
    //   • The module registry — `{ 'react': React, '@mui/material': {...}, ... }`
    //     the server's IIFE indexes by specifier (`__scope['@mui/material'].Box`).
    //   • The default JSX scope — `{ React, useState, ... }` for any code
    //     paths that reference top-level identifiers without an import.
    //   • Any caller-provided extras (host injections).
    //
    // The module registry is loaded lazily on first liveTsx mount — the
    // initial dashboard-app bundle excludes the ~5 MB of @mui/material,
    // @mui/icons-material, recharts namespaces, and only fetches them when
    // an LLM-emitted block actually needs to render. `getModuleRegistry`'s
    // internal promise cache means subsequent blocks in the same session
    // resolve synchronously.
    const [registry, setRegistry] = useState<Record<string, Record<string, unknown>> | null>(null);
    useEffect(() => {
        let cancelled = false;
        getModuleRegistry().then((r) => {
            if (!cancelled) setRegistry(r);
        }).catch((err) => {
            console.error('[LiveJsxBlock] Failed to load module registry:', err);
        });
        return () => { cancelled = true; };
    }, []);

    const scope = useMemo(() => {
        if (!registry) return null;
        const defaults = getDefaultJsxScope();
        return { ...registry, ...defaults, ...(extraScope ?? {}) };
    }, [registry, extraScope]);

    const result = useMemo(() => {
        if (partial || !scope) return { Component: null, error: null };
        try {
            const Component = compileToComponent(code, scope);
            return { Component, error: null };
        } catch (err) {
            return {
                Component: null,
                error: err instanceof Error ? err : new Error(String(err)),
            };
        }
    }, [code, partial, scope]);

    const errorFallback = (err: Error, isPartial?: boolean) => (
        <div style={{
            border: `1px solid ${isPartial ? 'lightgrey' : '#f44336'}`,
            borderRadius: 6,
            padding: '12px 16px',
            margin: '8px 0',
            backgroundColor: isPartial ? '#222' : 'rgba(244,67,54,0.06)',
        }}>
            {/* `whiteSpace: pre-wrap` so multi-line diagnostic messages (e.g.
                the registry pre-flight's bulleted list of missing modules)
                render with their original line breaks instead of collapsing
                into a single illegible run. */}
            <div style={{
                color: isPartial ? 'grey' : '#f44336',
                fontSize: '0.85em',
                fontWeight: 500,
                marginBottom: 6,
                whiteSpace: 'pre-wrap',
                fontFamily: err.message.includes('\n') ? 'monospace' : undefined,
            }}>
                JSX render error: {err.message}
            </div>
            <pre style={{ fontSize: '0.8em', overflow: 'auto', margin: 0, opacity: 0.7, whiteSpace: 'pre-wrap' }}>
                <code>{code}</code>
            </pre>
        </div>
    );

    const theme = useTheme();

    // Phase machine for the skeleton → final handoff. Without this,
    // the skeleton unmounts and the (usually taller) final pops into
    // place — the surrounding layout jumps and the user sees a hard
    // swap. By keeping the wrapper persistent and overlapping the
    // two children during the cross-fade window, the final reads as
    // "growing out of" the skeleton rather than replacing it.
    //   • partial  — only the skeleton is mounted; wrapper height
    //                is the skeleton's natural height.
    //   • arriving — both children are mounted, stacked (skeleton
    //                absolutely positioned over the final), opacity
    //                crosses 1→0 / 0→1, the wrapper's inline height
    //                animates from H1 (captured skeleton height) to
    //                H2 (measured final height).
    //   • final    — only the compiled component remains; inline
    //                height/transition are cleared so the layout can
    //                respond to subsequent content changes normally.
    type Phase = 'partial' | 'arriving' | 'final';
    const isPartial = partial || !scope;
    const isError = !isPartial && !!result.error;
    const [phase, setPhase] = useState<Phase>(() => (isPartial ? 'partial' : 'final'));
    const wrapperRef = useRef<HTMLDivElement>(null);
    const finalRef = useRef<HTMLDivElement>(null);

    // Trigger partial → arriving (and handle the reverse, e.g. if a
    // session message flips back to partial). `useLayoutEffect` so
    // we capture H1 *before* the browser paints the swap and never
    // see a frame where the wrapper has already collapsed.
    useLayoutEffect(() => {
        if (phase === 'partial' && !isPartial && !isError) {
            const wrapper = wrapperRef.current;
            const H1 = wrapper?.offsetHeight ?? 0;
            if (wrapper && H1 > 0) {
                wrapper.style.height = `${H1}px`;
            }
            setPhase('arriving');
        } else if (phase !== 'partial' && isPartial) {
            const wrapper = wrapperRef.current;
            if (wrapper) {
                wrapper.style.height = '';
                wrapper.style.transition = '';
            }
            setPhase('partial');
        }
    }, [phase, isPartial, isError]);

    // Once arriving, measure the final's natural height and animate
    // the wrapper from H1 → H2 over the same window as the opacity
    // cross-fade. The setTimeout cleanup matches the transition
    // duration plus a small buffer; on unmount or phase change the
    // pending callback is cancelled so we don't leak state writes.
    useLayoutEffect(() => {
        if (phase !== 'arriving') return;
        const wrapper = wrapperRef.current;
        const final = finalRef.current;
        if (!wrapper || !final) return;

        const H2 = final.offsetHeight;
        // Force reflow so the H1 inline value committed in the
        // previous effect is actually the transition's starting
        // computed value, not the new H2.
        void wrapper.offsetHeight;
        wrapper.style.transition = 'height 1500ms cubic-bezier(0.16, 1, 0.3, 1)';
        wrapper.style.height = `${H2}px`;

        const t = window.setTimeout(() => {
            const w = wrapperRef.current;
            if (w) {
                w.style.height = '';
                w.style.transition = '';
            }
            setPhase('final');
        }, 1500);
        return () => window.clearTimeout(t);
    }, [phase]);

    // Sentinel from the server cancels a partial block — keeps the
    // placeholder from animating forever when the build retry is about
    // to emit a fresh fence.
    if (code.includes(LIVE_TSX_INVALID_SENTINEL)) return null;

    // Compile errors skip the cross-fade and render the fallback
    // directly. The error path is rare and benefits more from being
    // immediate (so the user sees the diagnostic right away) than
    // from being eased in.
    if (isError && result.error) return errorFallback(result.error, false);

    // We prefer a shape-specific skeleton (ChartSkeleton/MetricSkeleton/
    // etc.) when `detectSkeleton` can read the intent from the partial
    // code; otherwise show the animated "Gerando visualização" card.
    const SkeletonComponent = detectSkeleton(code);
    const skeletonChild = SkeletonComponent
        ? <SkeletonComponent />
        : <GeneratingVisualization progress={progress} bytes={bytes} />;

    const finalChild = (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <JsxErrorBoundary fallback={errorFallback}>
                {/* `data-live-jsx-content` marks this subtree so the
                  * streaming-arrival animation in CurrentMessage can
                  * exclude it. The wrapper Box has stable identity
                  * across `code` / `scope` updates (it lives in
                  * LiveJsxBlock, not inside the compiled Component),
                  * so the inner children don't replay the fade. */}
                <Box data-live-jsx-content="">
                    {result.Component
                        ? <result.Component />
                        : <MarkdownExtended text={`\`\`\`tsx\n${code}\`\`\``} sessionId={''} />}
                </Box>
            </JsxErrorBoundary>
        </ThemeProvider>
    );

    return (
        <Box
            ref={wrapperRef}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                '@keyframes liveJsxBlockGrowIn': {
                    '0%': { opacity: 0, transform: 'scale(0.94)' },
                    '100%': { opacity: 1, transform: 'scale(1)' },
                },
            }}
        >
            {(phase === 'partial' || phase === 'arriving') && (
                <div
                    key="skeleton-layer"
                    style={{
                        // Absolute during arriving so the wrapper's
                        // height is controlled by the inline px value
                        // (animating H1 → H2) and not by whichever
                        // child happens to be taller this frame.
                        position: phase === 'arriving' ? 'absolute' : 'relative',
                        top: 0, left: 0, right: 0,
                        opacity: phase === 'arriving' ? 0 : 1,
                        transform: phase === 'arriving' ? 'scale(0.97)' : 'scale(1)',
                        transformOrigin: 'center top',
                        transition: phase === 'arriving'
                            ? 'opacity 360ms ease-out, transform 520ms cubic-bezier(0.4, 0, 0.6, 1)'
                            : 'none',
                        pointerEvents: phase === 'arriving' ? 'none' : 'auto',
                    }}
                >
                    {skeletonChild}
                </div>
            )}
            {(phase === 'arriving' || phase === 'final') && (
                <Box
                    key="final-layer"
                    ref={finalRef}
                    sx={{
                        animation: phase === 'arriving'
                            ? 'liveJsxBlockGrowIn 1000ms 0ms cubic-bezier(0.16, 1, 0.3, 1) both'
                            : 'none',
                        '@media (prefers-reduced-motion: reduce)': {
                            animation: 'none',
                        },
                    }}
                >
                    {finalChild}
                </Box>
            )}
        </Box>
    );
};

export default LiveJsxBlock;
